import { S3 } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import axios from 'axios';
import { useCallback, useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { isAuthenticated } from "./Auth";
import Loader from "./Loader";

import TextField from '@mui/material/TextField';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';

import Home from "Home";

const bucket = process.env.REACT_APP_UPLOAD_BUCKET;
const region = process.env.REACT_APP_REGION;

function CustomToolbar() {
  return (
      <GridToolbarContainer>
          <GridToolbarExport />
      </GridToolbarContainer>
  );
}

async function prepareColumns(headers){
  console.log('-----------> preparing columns', headers);
  let cols = [];
  for(let i = 0; i < headers.length; i++ ){
      let column = headers[i];
      let tmpCol = {};
      tmpCol.field = column;
      tmpCol.headerName = column;
      cols.push(tmpCol);
  }
  let id = { field: 'id', headerName: 'ID'};
  cols.push(id);
  return cols;
}

const csvJSON = async (csv) => {
  console.log('GETTING CSV', csv);
  var lines=csv.split("\n");
  var result = [];
  var headers=lines[0].split(",");

  console.log('Total lines:', lines.length);
  console.log('First 3 lines:', lines.slice(0, 3));
  console.log('Last 3 lines:', lines.slice(-3));

  for(var i=1;i<lines.length;i++){
      var obj = {};
      obj['id'] = i;
      var currentline=lines[i].split(",");
      for(var j=0;j<headers.length;j++){
          obj[headers[j].toString()] = currentline[j];
      }
      result.push(obj);
  }
  let retVal = {};
  retVal.headers = headers;
  retVal.rows = result;
  console.log('RESULT', result);
  return retVal;
}

const FileUpload = () => {

  const baseUri = process.env.REACT_APP_API_URL;
  const credsUrl = `${baseUri}/credentials`;
  const hostedUi = process.env.REACT_APP_HOSTED_UI;
  const { id } = useParams();

  const initialValues = { type: "" };
  const [values, setValues] = useState(initialValues);
  const [type, setType] = useState('');
  const [title, setTitle] = useState('');
  const [open, setOpen] = useState(false);
  const [auth, setAuth] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  
  // Upload status states
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadId, setUploadId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const showModal = async () => setOpen(true);
  const hideModal = async () => setOpen(false);

  // Poll upload status
  const pollUploadStatus = async (uploadIdentifier) => {
    const statusEndpoint = `${baseUri}/upload-status/${uploadIdentifier}`;
    let pollCount = 0;
    let errorCount = 0;
    const maxPolls = 150; // Max 5 minutes (150 * 2 seconds)
    const maxErrors = 5; // Stop after 5 consecutive errors
    
    const checkStatus = async () => {
      try {
        const response = await axios.get(statusEndpoint, { withCredentials: true });
        const status = response.data;
        
        console.log('Upload status:', status);
        setUploadStatus(status);
        pollCount++;
        errorCount = 0; // Reset error count on success
        
        if (status.status === 'completed') {
          setIsUploading(false);
          setUploadProgress(100);
          return true; // Stop polling
        } else if (status.status === 'failed' || status.status === 'error') {
          setIsUploading(false);
          setUploadError(status.errors || 'Upload failed. Please contact support with upload ID: ' + uploadIdentifier);
          return true; // Stop polling
        } else if (pollCount >= maxPolls) {
          setIsUploading(false);
          setUploadError('Upload is taking longer than expected. Upload ID: ' + uploadIdentifier + '. Please contact support.');
          return true; // Stop polling after timeout
        } else {
          // Still processing
          if (status.percent) {
            setUploadProgress(Math.max(50, status.percent)); // At least 50% (S3 upload done)
          }
          return false; // Continue polling
        }
      } catch (error) {
        console.error('Error checking upload status:', error);
        errorCount++;
        pollCount++;
        
        // Build detailed error message
        let errorMsg = '';
        if (error.response) {
          const statusCode = error.response.status;
          
          if (statusCode === 500) {
            // Record not found yet, keep polling
            console.log('Status record not found yet, continuing to poll...');
            return false;
          } else if (statusCode === 502) {
            errorMsg = `Server error (502 Bad Gateway) when checking upload status. `;
          } else if (statusCode === 503) {
            errorMsg = `Service temporarily unavailable (503). `;
          } else if (statusCode === 401 || statusCode === 403) {
            errorMsg = `Authentication error (${statusCode}). Please try logging in again. `;
          } else {
            errorMsg = `HTTP Error ${statusCode}: ${error.response.statusText || 'Unknown error'}. `;
          }
        } else if (error.request) {
          errorMsg = 'Network error: Unable to reach the server. ';
        } else {
          errorMsg = `Error: ${error.message}. `;
        }
        
        // If we've had too many errors, stop polling
        if (errorCount >= maxErrors) {
          errorMsg += `Failed after ${maxErrors} attempts. Upload ID: ${uploadIdentifier}. Please contact support.`;
          setUploadError(errorMsg);
          setIsUploading(false);
          return true; // Stop polling
        }
        
        // Otherwise, keep trying but log the error
        console.log(`Error ${errorCount}/${maxErrors}, continuing to poll...`);
        return false; // Continue polling
      }
    };

    // Poll every 2 seconds
    const pollInterval = setInterval(async () => {
      const shouldStop = await checkStatus();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
    }, 2000);

    // Initial check
    await checkStatus();
  };

  const upload = async (fileToUpload) => {
    // Reset states
    setUploadError(null);
    setUploadStatus(null);
    setUploadProgress(0);
    
    var file = fileToUpload.target.files[0];
    if (!file) {
      setUploadError('No file selected');
      return;
    }

    console.log('Starting upload:', file);
    setIsUploading(true);
    showModal();

    try {
      // Get temporary credentials
      let creds = await axios.get(credsUrl, { withCredentials: true });
      console.log('Got credentials');

      // Create unique ID for this upload
      const identifier = uuidv4();
      setUploadId(identifier);
      console.log('UPLOADING', type, identifier);

      const target = {
        Bucket: bucket,
        Key: `${identifier}.csv`,
        Body: file,
        Tagging: `type=${encodeURIComponent(type)}&identifier=${encodeURIComponent(identifier)}`,
        Metadata: {
          type: type,
          identifier: identifier
        }
      };

      console.log('S3 upload config:', {
        bucket,
        key: `${identifier}.csv`,
        fileSize: file.size,
        fileName: file.name
      });

      // Format credentials properly for AWS SDK
      const s3Client = new S3({
        region: region,
        credentials: {
          accessKeyId: creds.data.AccessKeyId || creds.data.accessKeyId,
          secretAccessKey: creds.data.SecretAccessKey || creds.data.secretAccessKey,
          sessionToken: creds.data.SessionToken || creds.data.sessionToken,
        }
      });

      console.log('Creating S3 upload...');

      const parallelUploads3 = new Upload({
        client: s3Client,
        params: target,
        leavePartsOnError: false,
      });

      console.log('Upload object created, starting upload...');

      // Track S3 upload progress
      parallelUploads3.on("httpUploadProgress", (progress) => {
        console.log('Upload progress event:', progress);
        if (progress.loaded && progress.total) {
          const percentComplete = Math.round((progress.loaded / progress.total) * 50); // 0-50% for S3 upload
          setUploadProgress(percentComplete);
          console.log('S3 Upload progress:', percentComplete + '%', `(${progress.loaded}/${progress.total} bytes)`);
        }
      });

      // Wait for S3 upload to complete
      console.log('Waiting for upload to complete...');
      const uploadResult = await parallelUploads3.done();
      console.log('S3 upload result:', uploadResult);
      console.log('S3 upload completed successfully');
      
      if (uploadResult && uploadResult.Location) {
        console.log('File uploaded to:', uploadResult.Location);
      }
      
      setUploadProgress(50); // S3 done, now processing starts

      // Start polling for processing status
      await pollUploadStatus(identifier);

    } catch (error) {
      console.error('Upload error:', error);
      
      let errorMessage = 'Upload failed: ';
      if (error.response) {
        errorMessage += error.response.data?.error || error.response.statusText;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Unknown error occurred';
      }
      
      if (uploadId) {
        errorMessage += `. Upload ID: ${uploadId}. Please contact support with this ID.`;
      }
      
      setUploadError(errorMessage);
      setIsUploading(false);
    } finally {
      hideModal();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  let initUpload = useCallback(async () => {
    showModal();

    console.log('DATASET ID', id);
    let isAuth = await isAuthenticated();
    setAuth(isAuth);
    console.log('AUTH',isAuth);

    let utendpoint = `${baseUri}/table/table/upload_types`;
    let uploadType = '';
    
    try {
      let uploadTypes = await axios.get(utendpoint);
      for(let i = 0; i < uploadTypes.data.rows.length; i++){
        let t = uploadTypes.data.rows[i];
        if(t.id.toString() === id.toString()){
          console.log('CURRENT UPLOAD TYPE', t);
          setTitle(t.name);
          console.log(t.type);
          uploadType = t.type;
          setType(uploadType);
        }
      }
    } catch(e){
      console.log(e);
    }

    let tableEndpoint = `${baseUri}/download/${uploadType}?limit=10`;
    console.log(tableEndpoint);
    
    try {
      let tableData = await axios.get(tableEndpoint);
      console.log('TABLE DATA',tableData);
      let file = tableData.data;
      let csv = await csvJSON(file);
      console.log('CSV',csv);
      let gridCols = await prepareColumns(csv.headers);
      console.log('GRID Cols', gridCols);
      console.log('CSV ROWS', csv.rows);
      setRows(csv.rows);
      setColumns(gridCols);
    } catch(e){
      console.log(e.message);
    }

    try {
      await axios.get(credsUrl,{ withCredentials: true });
      console.log('User is authenticated');
    } catch(e){
      console.log('User authentication check failed:', e);
    }
    
    hideModal();
  }, [baseUri, credsUrl, id]);

  useEffect(() => {
    initUpload();
  }, [initUpload]);

  return (
    <ul>
      {isAuthenticated ? (
        <div>
          <Container>
            <Loader open={open} />
            <h3>Upload Data</h3>
            <div>{title}</div>

            {!auth && (
              <div><a href={hostedUi}>login</a></div>
            )}

            <div>
              <TextField
                id="type-key"
                label="Type"
                variant="outlined"
                value={type}
                name="type"
                onChange={handleInputChange}
                className="App-textarea"
              />
            </div>

            <div>
              <input 
                type="file" 
                onChange={upload} 
                accept=".csv" 
                disabled={isUploading} 
              />
            </div>

            {/* Upload Progress */}
            {isUploading && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {uploadProgress < 50 ? 'Uploading file...' : 'Processing data...'} {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            {/* Upload Status */}
            {uploadStatus && uploadStatus.status === 'completed' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Upload completed successfully! Processed {uploadStatus.numRecords} records.
              </Alert>
            )}

            {/* Upload Error */}
            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <strong>Error:</strong> {uploadError}
              </Alert>
            )}

            {/* Current Status Info */}
            {uploadStatus && uploadStatus.status !== 'completed' && !uploadError && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Status: {uploadStatus.status} | Records: {uploadStatus.numRecords || 0} | Progress: {uploadStatus.percent || 0}%
                {uploadStatus.lastUpdated && <div>Last updated: {uploadStatus.lastUpdated}</div>}
              </Alert>
            )}

            <div style={{ paddingTop: 20 }}>
              <Box sx={{ height: 500, width: '100%', backgroundColor: '#ffffff' }}>
                <DataGrid
                  columns={columns}
                  rows={rows}
                  components={{
                    Toolbar: CustomToolbar,
                  }}
                  initialState={{
                    columns: {
                      columnVisibilityModel: {
                        id: false
                      },
                    },
                  }}
                />
              </Box>
            </div>
          </Container>
        </div>
      ) : (
        <Home />
      )}
    </ul>
  );
};

export default FileUpload;